import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import theme from '@splashthat/sui/lib/theme';
import { subheadlineLargeFontSize, subheadlineSmallFontSize } from 'common/styles/typography';

import Cube, { ANIMATION_DURATION, ANIMATION_STEPS } from 'common/components/Cube/Cube';

// Used when loading events or between pages
export const FULLSCREEN_SIZE = 'fullscreen';
// Used when loading views within the paper workspace or when loading nav items
export const DASHBOARD_SIZE = 'dashboard';
// Used within modals
export const MODAL_SIZE = 'modal';
// Used when loading inline items (e.g. elements within event filter menu)
export const INLINE_SIZE = 'inline';
// Used within buttons
export const BUTTON_SIZE = 'button';
export const BUTTON_SIZE_NO_PADDING = 'button-no-padding';
// Used within a generic context (medium size, no padding)
export const GENERIC_SIZE = 'generic';

export const DEFAULT_LOADER_DELAY = 300; // in ms
const baseClassName = 'CubeLoader';

// Modifiers for correct proportions when given different size values
const widthMod = 3.6;
const cubeMod = 0.75;
const paddingMod = 0.1;

// Sizes and padding for each style specified to fit design
const loaderStyles = {
    [FULLSCREEN_SIZE]: { size: 80, yPad: 60, xPad: 120 },
    [DASHBOARD_SIZE]: { size: 40, yPad: 60, xPad: 120 },
    [MODAL_SIZE]: { size: 30, yPad: 45, xPad: 80 },
    [GENERIC_SIZE]: { size: 30, yPad: 0, xPad: 0 },
    [INLINE_SIZE]: { size: 14, yPad: 20, xPad: 50 },
    [BUTTON_SIZE]: { size: 10, yPad: 10, xPad: 20 },
    [BUTTON_SIZE_NO_PADDING]: { size: 10, yPad: 0, xPad: 0 },
};

const { primary, warning, grayscale } = theme.palette;
const loaderColors = {
    default: {
        top: primary.main,
        bottom: primary.main,
        left: warning.main,
        right: warning.main,
        front: primary.main,
        back: primary.main,
    },
    alternate: grayscale.gray10,
};

const loaderImages = {
    default: {
        top: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_01_optimized-350.png',
        bottom: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_02_optimized-350.png',
        left: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_05_optimized-350.png',
        right: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_06_optimized-350.png',
        front: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_03_optimized-350.png',
        back: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_04_optimized-350.png',
    },
    alternate: {
        top: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_01_w_optimized-350.png',
        bottom: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_02_w_optimized-350.png',
        left: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_05_w_optimized-350.png',
        right: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_06_w_optimized-350.png',
        front: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_03_w_optimized-350.png',
        back: 'https://d24wuq6o951i2g.cloudfront.net/img/site-assets/cube_texture_04_w_optimized-350.png',
    },
};

const containerAnimation = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`;

const Container = styled.div`
    padding: ${(props) => props.yPad}px ${(props) => props.xPad}px;
    opacity: 0;
    animation: ${containerAnimation} 0s ${(props) => props.delay}ms forwards;
`;

const AnimationContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
    width: ${(props) => props.size * widthMod}px;
    padding: ${(props) => props.size * paddingMod}px;
    margin: auto;
`;

const TextContainer = styled.div`
    position: relative;
    text-align: center;
    font-size: ${subheadlineLargeFontSize};
    color: ${(props) => props.theme.palette.primary.main};
    padding-top: ${(props) => props.padding}px;
`;

const PreText = styled.p`
    text-transform: uppercase;
    font-size: ${subheadlineSmallFontSize};
`;

const CubeLoader = ({ className, delay, loading, preText, size, text, useAlternate }) => {
    if (!loading) {
        return null;
    }

    const style = loaderStyles[size];

    let textElement;
    if (text && [FULLSCREEN_SIZE, DASHBOARD_SIZE].includes(size)) {
        textElement = (
            <TextContainer className={`${baseClassName}__text`} padding={style.size / 2}>
                <PreText>{size === FULLSCREEN_SIZE ? preText : ''}</PreText>
                <p>{text}</p>
            </TextContainer>
        );
    }

    return (
        <Container className={`${baseClassName} ${className}`} delay={delay} xPad={style.xPad} yPad={style.yPad}>
            <AnimationContainer className={`${baseClassName}__animation`} size={style.size}>
                <Cube
                    animationDelay={-ANIMATION_DURATION / ANIMATION_STEPS}
                    colors={useAlternate ? loaderColors.alternate : loaderColors.default}
                    images={useAlternate ? loaderImages.alternate : loaderImages.default}
                    size={style.size * cubeMod}
                />
                <Cube
                    colors={useAlternate ? loaderColors.alternate : loaderColors.default}
                    images={useAlternate ? loaderImages.alternate : loaderImages.default}
                    size={style.size * cubeMod}
                />
                <Cube
                    animationDelay={-ANIMATION_DURATION / ANIMATION_STEPS}
                    colors={useAlternate ? loaderColors.alternate : loaderColors.default}
                    images={useAlternate ? loaderImages.alternate : loaderImages.default}
                    size={style.size * cubeMod}
                />
            </AnimationContainer>
            {textElement}
        </Container>
    );
};

CubeLoader.propTypes = {
    // [string] Additional classes to be added to the .CubeLoader container div
    className: PropTypes.string,
    // [integer] Sets the delay before the loader is displayed (in ms)
    delay: PropTypes.number,
    // [boolean] Determines whether or not the loader is rendered
    loading: PropTypes.bool,
    // [string] Smaller text displayed above the normal text. Only to be used with FULLSCREEN_SIZE
    preText: PropTypes.string,
    // [string] Determines the size and styling of the loader
    size: PropTypes.oneOf(Object.keys(loaderStyles)),
    // [string] Text displayed below the loader. Works with DASHBOARD_SIZE and FULLSCREEN_SIZE
    text: PropTypes.string,
    // [boolean] If present, the loader will use the white alternate textures for the cubes
    useAlternate: PropTypes.bool,
};

CubeLoader.defaultProps = {
    className: '',
    delay: DEFAULT_LOADER_DELAY,
    loading: true,
    preText: '',
    size: DASHBOARD_SIZE,
    text: '',
    useAlternate: false,
};

export default CubeLoader;
